import type { PriceVenue, Resolution } from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import { useQuery } from '@tanstack/react-query';
import { useIsPriceHistoryV2Enabled } from '@terminal/services/price/price-history-flags';
import { fetchPrices } from '@terminal/services/price/price-service';

const QUERY_KEY_PRICE_HISTORY = 'QUERY_KEY_PRICE_HISTORY';

interface UsePriceHistoryProps extends PriceVenue {
  entityTag: string;
  resolution?: Resolution;
  dateFrom?: string;
  dateTo?: string;
  ticker?: string;
  enabled?: boolean;
}

export const usePriceHistory = ({
  entityTag,
  resolution = '1D',
  dateFrom,
  dateTo,
  ticker,
  exchange,
  country,
  enabled = true,
}: UsePriceHistoryProps) => {
  // The flag selects v1 vs v2 at fetch time. v1 ignores exchange/country, so
  // only key on the venue when v2 is on — otherwise a late venue resolution
  // (undefined -> resolved once the entity settles) churns the key and refetches
  // the same v1 data.
  const isV2 = useIsPriceHistoryV2Enabled();

  return useQuery({
    queryKey: [
      QUERY_KEY_PRICE_HISTORY,
      entityTag,
      resolution,
      dateFrom,
      dateTo,
      ticker,
      isV2 ? exchange : undefined,
      isV2 ? country : undefined,
      // Key on the flag too so a mid-session flag change can't reuse a response
      // from the other API version.
      isV2,
    ],
    queryFn: () =>
      fetchPrices({
        entityTag,
        resolution,
        dateFrom,
        dateTo,
        ticker,
        exchange,
        country,
      }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled,
  });
};
